import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Packages
import { Button, Popconfirm, Space, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import { DeleteFilled, EditFilled, OrderedListOutlined, QuestionCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Components
import BooleanIcon from '../../utils/BooleanIcon';
import ImageRender from '../../utils/ImageRender';
import WorldRecipesOrder from './WorldRecipesOrder';

// Actions
import { get_all_worlds, world_delete } from '../../../redux/actions/worldsActions';
import { transform_color_format } from '../../../utils/colors_handlers';
import { object_ref_id } from '../../../utils/world_medals';
import { star_goal_flags } from '../../../utils/star_goals';

const WorldsTable = (props) => {
	const { messageApi, pageSize, setPageSize, skip, setSkip } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const navigate = useNavigate();

	const { all_worlds_loading, all_worlds, world_delete_loading } = useSelector(state => state.worlds);

	const { all_recipes } = useSelector(state => state.recipes);

	const { all_packages } = useSelector(state => state.packages);

	const [showRecipesOrder, setShowRecipesOrder] = useState(false);
	const [orderWorld, setOrderWorld] = useState({});

	// Star-goal chain health over the map order (green/amber/red per world)
	const sortedWorlds = [...(all_worlds?.worlds ?? [])].sort(
		(a, b) => (a.order ?? 0) - (b.order ?? 0)
	);
	const goalFlags = star_goal_flags(
		sortedWorlds, all_recipes?.recipes, all_packages?.packages
	);

	const openRecipesOrder = (record) => {
		setOrderWorld(record);
		setShowRecipesOrder(true);
	}

	const update = (record) => {
		navigate(`/game/worlds/${record._id.$oid}/details`);
	}

	const deleteWorld = (record) => {
		let newSkip = skip;

		if ((all_worlds.count - 1) % pageSize === 0) {
			newSkip -= pageSize;
			setSkip(newSkip);
			localStorage.setItem('worldsSkip', newSkip);
		}

		const filters = { skip: newSkip, limit: pageSize };
		dispatch(world_delete(messageApi, record._id.$oid, filters));
	}

	const columns = [
		{
			key: 'image',
			title: t('form.label.image'),
			dataIndex: 'image',
			render: (_, record) => (
				<ImageRender
					key={record.image}
					url={`${process.env.REACT_APP_SERVER_URL}/api/game/worlds/${record._id.$oid}/image`}
					size={{ width: 50, height: 50 }}
				/>
			)
		},
		{
			key: 'name',
			title: t('form.label.name'),
			dataIndex: 'name',
			render: (_, record) => (
				<Typography.Text copyable={{ text: record._id.$oid, tooltips: [t('table.copyableText.copyId'), t('table.copyableText.copiedId')] }}>
					{record.name}
				</Typography.Text>
			)
		},
		{
			key: 'description',
			title: t('form.label.description'),
			dataIndex: 'description'
		},
		{
			key: 'nationality',
			title: t('form.label.nationality'),
			dataIndex: 'nationality'
		},
		{
			key: 'order',
			title: t('form.label.order'),
			dataIndex: 'order',
			render: (order) => order ?? 0
		},
		{
			key: 'star_goal',
			title: t('form.label.star_goal'),
			dataIndex: 'star_goal',
			render: (starGoal, record) => {
				const flag = goalFlags[object_ref_id(record._id)] ?? 'ok';
				if (flag === 'premium') {
					return <Tag color='gold'>{t('worlds.details.premiumTag')}</Tag>;
				}
				const colors = { ok: 'green', warn: 'orange', error: 'red' };
				const tips = {
					warn: t('worlds.details.goalNotIncreasing'),
					error: t('worlds.details.goalUnreachable')
				};
				const tag = <Tag color={colors[flag]}>{`⭐ ${starGoal ?? 0}`}</Tag>;
				return flag === 'ok'
					? tag
					: <Tooltip title={tips[flag]}>{tag}</Tooltip>;
			}
		},
		{
			key: 'color',
			title: t('form.label.color'),
			dataIndex: 'color',
			render: (_, record) => (
				record?.color !== undefined
					?	<Tag color={`#${transform_color_format(record?.color)}`}>{record?.color?.toUpperCase()}</Tag>
					:	<Tag>{t('worlds.noColor')}</Tag>
			)
		},
		{
			key: 'pattern',
			title: t('form.label.pattern'),
			dataIndex: 'pattern',
			render: (pattern) => <BooleanIcon value={pattern !== undefined && pattern !== null} />
		},
		{
			key: 'action',
			title: t('table.headers.actions'),
			dataIndex: 'action',
			render: (_, record) => (
				<Space>
					<Tooltip title={t('worlds.recipesOrder.buttonTooltip')}>
						<Button
							type='primary'
							style={{ backgroundColor: '#F18B71' }}
							shape='circle'
							size='large'
							icon={<OrderedListOutlined />}
							onClick={() => openRecipesOrder(record)}
						/>
					</Tooltip>
					<Tooltip title={t('buttons.edit')}>
						<Button
							type='primary'
							style={{ backgroundColor: '#80B4B9' }}
							shape='circle'
							size='large'
							icon={<EditFilled />}
							onClick={() => update(record)}
						/>
					</Tooltip>
					<Popconfirm
						title={t('worlds.delete.confirmTitle')}
						description={t('worlds.delete.confirmText')}
						icon={<QuestionCircleFilled style={{ color: '#1677FF' }} />}
						onConfirm={() => deleteWorld(record)}
						okButtonProps={{
							loading: world_delete_loading
						}}
						okText={t('buttons.confirm')}
						cancelText={t('buttons.cancel')}
					>
						<Button
							type='primary'
							shape='circle'
							size='large'
							danger
							icon={<DeleteFilled />}
						/>
					</Popconfirm>
				</Space>
			)
		}
	];

	const handleTableChange = (pagination, filters, sorter) => {
		const skip = (pagination.current - 1) * pagination.pageSize;
		setSkip(skip);
		localStorage.setItem('worldsSkip', skip);

		const limit = pagination.pageSize;
		setPageSize(limit);
		localStorage.setItem('worldsPageSize', limit);

		const worldsFilters = { skip, limit };
		dispatch(get_all_worlds(worldsFilters));
	};

	return (
		<Spin spinning={all_worlds_loading}>
			<WorldRecipesOrder
				messageApi={messageApi}
				isModalOpen={showRecipesOrder}
				setIsModalOpen={setShowRecipesOrder}
				world={orderWorld}
				setWorld={setOrderWorld}
			/>
			<Table
				columns={columns}
				dataSource={all_worlds.worlds}
				onChange={handleTableChange}
				scroll={{
					x: 'auto'
				}}
				pagination={{
					pageSize: pageSize, 
					total: all_worlds.count,
					current: (skip / pageSize) + 1,
					showSizeChanger: true,
					showTotal: (total, range) => `${range[0]} - ${range[1]} ${t('table.pagination.of')} ${total}`,
					showTitle: false,
					responsive: true,
					locale: { items_per_page: `/ ${t('table.pagination.page')}` }
				}}
			/>
		</Spin>
	)
}

WorldsTable.propTypes = {
	messageApi: PropTypes.object.isRequired,
	pageSize: PropTypes.number.isRequired,
	setPageSize: PropTypes.func.isRequired,
	skip: PropTypes.number.isRequired,
	setSkip: PropTypes.func.isRequired
}

export default WorldsTable;
